// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

// Re-export ID utilities
import { createId } from './utils/id'
export {
  createId,
  safeNanoid,
  ENTITY_TO_PREFIX,
  ID_PREFIX_TO_COLLECTION,
  getCollectionFromId,
  getEntityTypeFromId,
} from './utils/id'

// Utilities
export * from './utils'

// Query libs
export { getInbox, getAgentInbox } from './queries/agentInbox'
export type {
  TaskInboxItem,
  MentionInboxItem,
  ReplyInboxItem,
  ConversationInboxItem,
  ApprovalInboxItem,
  DescriptionMentionInboxItem,
  AgentInboxResult,
  InboxIdentity,
  AgentInboxIdentity,
  UserInboxIdentity,
  AgentInboxOpts,
  AgentInboxCollections,
} from './queries/agentInbox'
export {
  getDirectReports,
  getDescendants,
  assertCanControl,
  assertCanReport,
} from './queries/agentHierarchy'
export type {
  AgentHierarchyRecord,
  AgentHierarchyCollections,
  AgentHierarchyInput,
} from './queries/agentHierarchy'
export { getSubordinateReport } from './queries/subordinateReport'
export type {
  SubordinateReportOptions,
  SubordinateControlPlaneReport,
  SubordinateAgentReport,
  SubordinateInboxSummary,
  SubordinateBlockerReference,
  SubordinateReportCollections,
  SubordinateTeamMemberRecord,
  TeamMemberReportSource,
  AgentRecordSource,
} from './queries/subordinateReport'

// Zod validation schemas
export * from './schemas/index'

import { Collection as AppSnapshotCollection } from './models/AppSnapshot'
import { Collection as ApprovalCollection } from './models/Approval'
import { Collection as CodebaseCollection } from './models/Codebase'
import { Collection as DiscussionCollection } from './models/Discussion'
import { Collection as MilestoneCollection } from './models/Milestone'
import { Collection as OrgCollection } from './models/Org'
import { Collection as ProjectCollection } from './models/Project'
import { Collection as DocumentCollection } from './models/Document'
import { Collection as TaskCollection } from './models/Task'
import { Collection as TemplateCollection } from './models/Template'
import { Collection as UserCollection } from './models/User'
import { Collection as WorkspaceCollection } from './models/Workspace'
import { Collection as SchemaCollection } from './models/Schema'
import { Collection as RecordCollection } from './models/Record'
import { Collection as RelationCollection } from './models/Relation'
import { Collection as VaultCollection } from './models/Vault'
import { Collection as VaultItemCollection } from './models/VaultItem'
import { Collection as ServiceKeyCollection } from './models/ServiceKey'
import { Collection as WorkerCollection } from './models/Worker'
import { Collection as AttributeCollection } from './models/Attribute'
import { Collection as ActivityCollection } from './models/Activity'
import { Collection as TeamMemberCollection } from './models/TeamMember'
import { Collection as TimeEntryCollection } from './models/TimeEntry'
import { Collection as ConversationCollection } from './models/Conversation'
import { Collection as MessageCollection } from './models/Message'
import { Collection as GoalCollection } from './models/Goal'
import { Collection as ProcessesCollection } from './models/Processes'
import { Collection as ProcessExecutionsCollection } from './models/ProcessExecutions'
import { Collection as ProcessStepsCollection } from './models/ProcessSteps'
import { Collection as StepTemplatesCollection } from './models/StepTemplates'
import { Collection as RoleCollection } from './models/Role'
import { Collection as RoleMembershipCollection } from './models/RoleMembership'

// Export all models (using actual export names from each file)
export type { FinAccountModel } from './virtual/types/FinAccount'
export type { AppSnapshotModel } from './models/AppSnapshot'
export type { ApprovalModel } from './models/Approval'
export type { AttributeModel } from './models/Attribute'
export type { BudgetModel } from './virtual/types/FinBudget'
export type { FinCategoryModel } from './virtual/types/FinCategory'
export type { ClientModel } from './virtual/types/Client'
export type { CodebaseModel } from './models/Codebase'
export type { ContactModel } from './virtual/types/Contact'
export type { ConversationModel } from './models/Conversation'
export type { ConversationSettings, ConversationMetadata } from './types/conversation'
export type { DiscussionModel } from './models/Discussion'
export type { FlowModel } from './virtual/types/Flow'
export type { GoalModel } from './models/Goal'
export type {
  GoalMetric,
  GoalMetricType,
  GoalMetricTrend,
  GoalStatus,
  GoalProgressMode,
  GoalCategory,
} from './types/goal'
export type { MessageModel } from './models/Message'
export type { MessageMetadata, ToolCall } from './types/message'
export type { MilestoneModel } from './models/Milestone'
export type { MilestoneMetadata } from './types/milestone'
export type { OpportunityModel } from './virtual/types/Opportunity'
export type { OrgModel } from './models/Org'
export type { OrgMetadata } from './types/org'
export type {
  ProductModel,
  ProductType,
  ProductPhase,
  ProductKpi,
  KpiTrend,
  PhaseHistoryEntry,
  ProductMetadata,
} from './virtual/types/Product'
export type { ProjectModel } from './models/Project'
export type { ProjectMetadata } from './types/project'
export type { RoadmapModel } from './virtual/types/Roadmap'
export type { FinScheduledTransactionModel } from './virtual/types/FinScheduledTransaction'
export type {
  SessionModel,
  SessionContext,
  TerminalConnectionStatus,
  ShellType,
  SessionScopeEntityType,
} from './virtual/types/Session'
export type { DocumentModel } from './models/Document'
export type { DocumentStorageInfo } from './types/document'
export type { TaskModel } from './models/Task'
export type { TaskMetadata } from './types/task'
export type { TemplateModel } from './models/Template'
export type {
  TemplateCategory,
  TemplateItem,
  MilestoneTemplateItem,
  TaskTemplateItem,
  SubtaskTemplateItem,
  ProjectTemplateData,
} from './types/template'
export type { TimeEntryModel } from './models/TimeEntry'
export type { FinTransactionModel } from './virtual/types/FinTransaction'
export type {
  FinIncomeStatementModel,
  LinkedStatementRef,
  StatementPeriod,
} from './virtual/types/FinIncomeStatement'
export type { UserModel } from './models/User'
export type { VariablesModel } from './virtual/types/Variables'
export type { WorkspaceModel } from './models/Workspace'
export type { SyncConfigModel, SyncFieldMapping } from './virtual/types/SyncConfig'
export type { ProcessesModel } from './models/Processes'
// ProcessModel alias for backward compat
export type { ProcessesModel as ProcessModel } from './models/Processes'
export type {
  ProcessMetadata,
  ProcessTriggerType,
  ProcessTrigger,
  ProcessSchema,
  ProcessStatistics,
  ProcessExecutionConfig,
  QueueBehavior,
  FlowStateEntityTrigger,
  FlowStateTriggerCondition,
  TriggerConditionOperator,
  TriggerDebounceConfig,
  TriggerEntityType,
} from './types/process'
// ProcessStatus and ProcessCategory already in types.ts — re-export from there
export type { ProcessStatus, ProcessCategory } from './types'
export type { ProcessStepsModel } from './models/ProcessSteps'
// ProcessStepModel alias for backward compat
export type { ProcessStepsModel as ProcessStepModel } from './models/ProcessSteps'
export type {
  ProcessStepMetadata,
  ProcessStepStatus,
  StepAction,
  StepCondition,
  StepOutput,
  StepPosition,
  SubprocessConfig,
  SubprocessFailureStrategy,
  AgentTaskConfig,
  CommandConfig,
  ContainerConfig,
  ContainerVolumeMount,
  ContainerResources,
} from './types/processStep'
// ProcessStepType already in types.ts — re-export from there
export type { ProcessStepType } from './types'
export type { ProcessExecutionsModel } from './models/ProcessExecutions'
// ProcessExecutionModel alias for backward compat
export type { ProcessExecutionsModel as ProcessExecutionModel } from './models/ProcessExecutions'
export type {
  ProcessExecutionMetadata,
  ProcessExecutionStatus,
  StepExecutionRecord,
  ExecutionContext,
  TriggerSourceEntity,
} from './types/processExecution'
export type { StepTemplatesModel } from './models/StepTemplates'
// StepTemplateModel alias for backward compat
export type { StepTemplatesModel as StepTemplateModel } from './models/StepTemplates'
export type { ProductGoalModel } from './virtual/types/ProductGoal'
// generateActivityId was in hand-crafted Activity.ts; now Activity.ts is generated,
// so we define it here using the already-imported createId utility.
export const generateActivityId = (): string => createId('activity')
export type { ProductProjectModel, ProductProjectRole } from './virtual/types/ProductProject'
export type {
  ProductTeamMemberModel,
  ProductTeamMemberRole,
} from './virtual/types/ProductTeamMember'
export type { ActivityModel } from './models/Activity'
export type { ActivityEntityType, ActivityAction, ActivityChanges } from './types/activity'
export type { TeamMemberModel } from './models/TeamMember'
export type { TeamMemberRole, RaciType, RaciAssignment } from './types/teamMember'
export type { OrgThemeModel } from './virtual/types/OrgTheme'
export type { BusinessPlanModel } from './virtual/types/BusinessPlan'
export type {
  MarketAnalysisModel,
  MarketSize,
  TargetSegment,
  SwotAnalysis,
} from './virtual/types/MarketAnalysis'
export type { CompetitorModel } from './virtual/types/Competitor'
export type {
  OfferModel,
  OfferValueStackItem,
  OfferBonus,
  OfferStatus,
  OfferType,
  GuaranteeType,
  ScarcityType,
  UrgencyType,
} from './virtual/types/Offer'
export type {
  CampaignModel,
  CampaignType,
  CampaignStatus,
  CampaignChannel,
  CampaignGoalType,
} from './virtual/types/Campaign'
export type {
  AudienceModel,
  AudienceType,
  AudienceSource,
  AudiencePillar,
} from './virtual/types/Audience'
export type {
  LeadMagnetModel,
  LeadMagnetType,
  LeadMagnetDelivery,
  LeadMagnetStatus,
} from './virtual/types/LeadMagnet'
export type { CampaignContactModel, CampaignContactStatus } from './virtual/types/CampaignContact'
export type { CampaignProductModel, CampaignProductRole } from './virtual/types/CampaignProduct'
export type { DealModel, DealStage } from './virtual/types/Deal'
export type { PipelineModel } from './virtual/types/Pipeline'
export type { PipelineStageModel, PipelineStageType } from './virtual/types/PipelineStage'
export type { QuoteModel, QuoteStatus } from './virtual/types/Quote'
export type { InvoiceModel, InvoiceStatus } from './virtual/types/Invoice'
export type { LineItemModel, LineItemParentType } from './virtual/types/LineItem'
export type { DealProductModel } from './virtual/types/DealProduct'
export type { DealProjectModel, DealProjectRelationship } from './virtual/types/DealProject'
export type {
  PartnershipModel,
  PartnershipType,
  PartnershipStatus,
} from './virtual/types/Partnership'
export type {
  ContentPieceModel,
  ContentPieceStatus,
  ContentPieceType,
  ContentPlatform,
  ContentPieceMetrics,
  MessagingFramework,
  AwarenessLevel,
  ContentFunnelStageLevel,
  OutlineSection,
} from './virtual/types/ContentPiece'
export type { ContentPillarModel, PillarCategory } from './virtual/types/ContentPillar'
export type {
  HookTemplateModel,
  HookCategory,
  HookSource,
  HookVariable,
} from './virtual/types/HookTemplate'
export type { ConversionCodeModel, ConversionCategory } from './virtual/types/ConversionCode'
export type {
  ContentFunnelModel,
  FunnelStatus,
  FunnelStage,
  FunnelMetrics,
} from './virtual/types/ContentFunnel'
export type {
  PaymentPlanModel,
  PaymentPlanType,
  PaymentPlanStatus,
} from './virtual/types/PaymentPlan'
export type { OutreachTargetModel } from './virtual/types/OutreachTarget'
export type { OfferWorksheetModel, OfferWorksheetStatus } from './virtual/types/OfferWorksheet'
export type {
  OfferFunnelModel,
  OfferFunnelType,
  OfferFunnelStatus,
} from './virtual/types/OfferFunnel'
export type { SchemaModel } from './models/Schema'
export type {
  FieldDefinition,
  FieldConfig,
  FieldType,
  FieldRenderer,
  SelectOption,
  SchemaTarget,
  ClassLevelPermissions,
} from './types/schema'
export type { RoleModel } from './models/Role'
export type { RoleMembershipModel } from './models/RoleMembership'
export type { RecordModel } from './models/Record'
export type { RelationModel } from './models/Relation'
export type { VaultModel } from './models/Vault'
export type { VaultItemModel } from './models/VaultItem'
export type { VaultItemType } from './types/vaultItem'
export type { ServiceKeyModel } from './models/ServiceKey'
export type {
  TicketModel,
  TicketStatus,
  TicketSeverity,
  TicketType,
  TicketSource,
} from './virtual/types/Ticket'
export type {
  FeedbackItemModel,
  FeedbackType,
  FeedbackSentiment,
  FeedbackStatus,
} from './virtual/types/FeedbackItem'
export type {
  KbArticleModel,
  KbArticleVisibility,
  KbArticleStatus,
} from './virtual/types/KbArticle'
export type { HealthScoreModel, PaymentStatus, HealthTrend } from './virtual/types/HealthScore'
export type {
  IntegrationModel,
  IntegrationProductType,
  IntegrationStatus,
} from './virtual/types/Integration'
export type { WorkerModel } from './models/Worker'

// Export conversation management types
export type {
  ConversationTurn,
  TurnMetadata,
  ConversationState,
  CompressedHistory,
  ConversationConfig,
} from './types'

// Export YNAB/Financial types
export type {
  AccountType,
  LoanAccountPeriodicValue,
  SubtransactionSummary,
  ScheduledTransactionFrequency,
  TransactionFlagColor,
  TransactionFlagName,
  TransactionClearedStatus,
} from './types'

// Export Discussion types
export type { AttachmentModel, AttachmentStatus, DiscussionMention } from './types'

export { DEFAULT_CONVERSATION_CONFIG } from './types'

// Export core collection classes
export { Collection as AppSnapshotCollection } from './models/AppSnapshot'
export { Collection as ApprovalCollection } from './models/Approval'
export { Collection as CodebaseCollection } from './models/Codebase'
export { Collection as DiscussionCollection } from './models/Discussion'
export { Collection as MilestoneCollection } from './models/Milestone'
export { Collection as OrgCollection } from './models/Org'
export { Collection as ProjectCollection } from './models/Project'
export { Collection as DocumentCollection } from './models/Document'
export { Collection as TaskCollection } from './models/Task'
export { Collection as TemplateCollection } from './models/Template'
export { Collection as UserCollection } from './models/User'
export { Collection as WorkspaceCollection } from './models/Workspace'
export { Collection as SchemaCollection } from './models/Schema'
export { Collection as RecordCollection } from './models/Record'
export { Collection as RelationCollection } from './models/Relation'
export { Collection as VaultCollection } from './models/Vault'
export { Collection as VaultItemCollection } from './models/VaultItem'
export { Collection as ServiceKeyCollection } from './models/ServiceKey'
export { Collection as WorkerCollection } from './models/Worker'
export { Collection as AttributeCollection } from './models/Attribute'
export { Collection as ActivityCollection } from './models/Activity'
export { Collection as TeamMemberCollection } from './models/TeamMember'
export { Collection as TimeEntryCollection } from './models/TimeEntry'
export { Collection as ConversationCollection } from './models/Conversation'
export { Collection as MessageCollection } from './models/Message'
export { Collection as GoalCollection } from './models/Goal'
export { Collection as ProcessesCollection } from './models/Processes'
export { Collection as ProcessExecutionsCollection } from './models/ProcessExecutions'
export { Collection as ProcessStepsCollection } from './models/ProcessSteps'
export { Collection as StepTemplatesCollection } from './models/StepTemplates'
export { Collection as RoleCollection } from './models/Role'
export { Collection as RoleMembershipCollection } from './models/RoleMembership'

export { VirtualCollectionAdapter, wrapRxCollection } from './core/VirtualCollectionAdapter'
export type { RecordCollection as RecordCollectionInterface } from './core/VirtualCollectionAdapter'
export { SchemaRegistry } from './core/SchemaRegistry'
export {
  EncryptedCollectionAdapter,
  deriveEncryptedFieldsConfig,
} from './core/EncryptedCollectionAdapter'

export { migrateCollectionToRecords, migrateAll } from './migrations/migrateToRecords'
export type { MigrationResult } from './migrations/migrateToRecords'

export { seedBuiltInRoles } from './migrations/seedBuiltInRoles'
export { migrateTeamMembersToRoleMemberships } from './migrations/migrateTeamMembersToRoleMemberships'
export type {
  MigrationDependencies,
  MigrationResult as TeamMigrationResult,
} from './migrations/migrateTeamMembersToRoleMemberships'
export { promoteTeamMemberAgentFields } from './migrations/promoteTeamMemberAgentFields'
export type {
  PromotionDependencies,
  PromotionResult,
} from './migrations/promoteTeamMemberAgentFields'

export { coreSeeds, SEED_ORG_PLACEHOLDER, SEED_WORKSPACE_PLACEHOLDER } from './virtual'
export type { SeedSchema } from './virtual'
export { seedSchemas } from './virtual/seeds/runner'
export type { SeedResult, SeedOptions } from './virtual/seeds/runner'
export { seedBuiltInCLP } from './virtual/seeds/seedBuiltInCLP'
export type { SeedBuiltInCLPResult } from './virtual/seeds/seedBuiltInCLP'
export { DEFAULT_CLP, BUILT_IN_COLLECTION_NAMES } from './virtual/seeds/clpSeeds'

export {
  MigrationRegistry,
  runMigrations,
  allMigrations,
  createMigrationRegistry,
} from './virtual/migrations/index'
export type {
  SchemaMigration,
  SchemaMigrationResult,
  MigrationRunnerOptions,
  MigrationHistoryEntry,
} from './virtual/migrations/index'

// Collection tier selection (community vs premium)
export {
  COMMUNITY_COLLECTIONS,
  ALL_COLLECTION_NAMES,
  getCollectionsForTier,
  buildCollectionModes,
} from './tiers'
export type { CommunityCollectionName, CollectionName } from './tiers'

// Collection mode configuration (local/remote routing)
export type { CollectionModeConfig, CollectionMode } from './config/CollectionModeConfig'
export { getCollectionMode } from './config/CollectionModeConfig'

// Remote D1 adapters
export { D1Client, D1ClientError } from './adapters/remote/d1Client'
export type { D1QueryParams } from './adapters/remote/d1Client'
export { collectionKeys } from './adapters/remote/queryKeys'

export const collections = {
  [AppSnapshotCollection.name]: AppSnapshotCollection,
  [ApprovalCollection.name]: ApprovalCollection,
  [DiscussionCollection.name]: DiscussionCollection,
  [MilestoneCollection.name]: MilestoneCollection,
  [OrgCollection.name]: OrgCollection,
  [ProjectCollection.name]: ProjectCollection,
  [DocumentCollection.name]: DocumentCollection,
  [TaskCollection.name]: TaskCollection,
  [TemplateCollection.name]: TemplateCollection,
  [UserCollection.name]: UserCollection,
  [WorkspaceCollection.name]: WorkspaceCollection,
  [SchemaCollection.name]: SchemaCollection,
  [RecordCollection.name]: RecordCollection,
  [RelationCollection.name]: RelationCollection,
  [VaultCollection.name]: VaultCollection,
  [VaultItemCollection.name]: VaultItemCollection,
  [ServiceKeyCollection.name]: ServiceKeyCollection,
  [WorkerCollection.name]: WorkerCollection,
  [AttributeCollection.name]: AttributeCollection,
  [ActivityCollection.name]: ActivityCollection,
  [TeamMemberCollection.name]: TeamMemberCollection,
  [TimeEntryCollection.name]: TimeEntryCollection,
  [ConversationCollection.name]: ConversationCollection,
  [MessageCollection.name]: MessageCollection,
  [GoalCollection.name]: GoalCollection,
  [CodebaseCollection.name]: CodebaseCollection,
  [ProcessesCollection.name]: ProcessesCollection,
  [ProcessExecutionsCollection.name]: ProcessExecutionsCollection,
  [ProcessStepsCollection.name]: ProcessStepsCollection,
  [StepTemplatesCollection.name]: StepTemplatesCollection,
  [RoleCollection.name]: RoleCollection,
  [RoleMembershipCollection.name]: RoleMembershipCollection,
}
